import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Campo de texto.
 *
 * Existe porque busca e formulário apareciam montados à mão em várias telas,
 * cada um com sua altura. Aqui o alvo é 56px e o texto digitado tem o tamanho
 * do conteúdo, não o tamanho de uma etiqueta: um campo de busca com letra de
 * 13px não serve para quem está de óculos no mercado.
 *
 * O rótulo fica FORA e ACIMA do campo, sempre visível. Rótulo que vira
 * placeholder e desaparece quando a pessoa começa a digitar é uma economia de
 * espaço que custa a memória de quem preenche.
 */
export function Campo({
  rotulo,
  valor,
  onMudar,
  placeholder,
  icone,
  onLimpar,
  tipo = "text",
  inputMode,
  autoFocus,
  desabilitado,
  onTeclaEnter,
}: {
  rotulo?: string;
  valor: string;
  onMudar: (v: string) => void;
  placeholder?: string;
  icone?: LucideIcon;
  /** Quando existe, mostra um X de limpar com alvo próprio de 56px. */
  onLimpar?: () => void;
  tipo?: "text" | "search" | "number" | "email";
  inputMode?: "text" | "search" | "numeric" | "decimal" | "email";
  autoFocus?: boolean;
  desabilitado?: boolean;
  /** Enter confirma. Não substitui o botão: é atalho, não caminho único. */
  onTeclaEnter?: () => void;
}) {
  const { t } = useTranslation();
  const Icone = icone;
  return (
    <div style={{ paddingLeft: "var(--gutter)", paddingRight: "var(--gutter)" }}>
      {rotulo && (
        <label
          className="mb-2 block font-semibold"
          style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}
        >
          {rotulo}
        </label>
      )}
      <div
        className={`flex items-center gap-3 cartao px-4 ${
          desabilitado ? "opacity-50" : ""
        }`}
        style={{ minHeight: "var(--alvo-min)" }}
      >
        {Icone && (
          <Icone className="h-6 w-6 shrink-0" strokeWidth={2} style={{ color: "var(--tinta-apoio)" }} />
        )}
        <input
          type={tipo}
          inputMode={inputMode}
          value={valor}
          autoFocus={autoFocus}
          onChange={(e) => onMudar(e.target.value)}
          onKeyDown={onTeclaEnter && ((e) => { if (e.key === "Enter") onTeclaEnter(); })}
          disabled={desabilitado}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          style={{ fontSize: "var(--t-corpo)", minHeight: "var(--alvo-min)" }}
        />
        {onLimpar && valor.length > 0 && (
          <button
            type="button"
            onClick={onLimpar}
            aria-label={t("common.clear")}
            className="-mr-3 flex shrink-0 items-center justify-center"
            style={{ width: "var(--alvo-min)", height: "var(--alvo-min)" }}
          >
            <X className="h-6 w-6" strokeWidth={2.5} style={{ color: "var(--tinta-apoio)" }} />
          </button>
        )}
      </div>
    </div>
  );
}
